import { CaseVoucherExportField, caseVoucherExportFields, caseVoucherExportLabels, } from '@debbie/common/dist/economy/case-vouchers/export.js';
import { Entity, ExportType } from '@debbie/common/dist/index.js';
import { roles } from '@debbie/common/dist/users/roles.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import Button from '../../Button.js';
import Checkbox from '../../Checkbox.js';
import DraggableList from '../../DraggableList.js';
import { Check, Menu, Plus, Search, X } from '../../Icons/index.js';
import Input from '../../Input.js';
import Modal from '../../Modal.js';
import PlaceholderLoader from '../../PlaceholderLoader.js';
import { defaultExportSettings, caseVoucherExportSettingsSchema, exportColumnKey, } from './export-settings.js';
const CaseVoucherExportEditor = (props) => {
    const lang = useSelector((state) => state.lang);
    const role = useRole();
    const propertyTypes = usePropertyTypes();
    const [draft, setDraft] = useState(props.settings);
    const [search, setSearch] = useState('');
    const [group, setGroup] = useState('all');
    const labels = caseVoucherExportLabels(lang);
    const fieldNames = {
        ...labels,
        [CaseVoucherExportField.Type]: lang.deposits.addSingleDepositPaymentType,
        [CaseVoucherExportField.VoucherType]: lang.economyAppendixHeaders.voucherType,
    };
    const columns = [
        ...caseVoucherExportFields.map((field) => ({
            column: { type: 'field', field },
            label: fieldNames[field],
            group: 'standard',
            search: fieldNames[field],
        })),
        ...(h.hasData(propertyTypes)
            ? propertyTypes.data
                .filter((type) => [Entity.Creditor, Entity.Case, Entity.CaseVoucher].includes(type.ref) &&
                role != null &&
                roles.indexOf(role) >= roles.indexOf(type.permissionLevel))
                .map((type) => ({
                column: {
                    type: 'property',
                    propertyTypeId: type.propertyTypeId,
                    label: `${lang.entities[type.ref]}: ${type.label}`,
                },
                label: type.label,
                group: type.ref,
                search: `${type.label} ${type.handle} ${lang.entities[type.ref]}`,
            }))
            : []),
    ];
    const groups = [
        { id: 'standard', label: lang.deposits.standardExportColumns },
        ...[Entity.Creditor, Entity.Case, Entity.CaseVoucher].map((ref) => ({
            id: ref,
            label: lang.entities[ref],
        })),
    ];
    const chosen = new Set(draft.columns.map(exportColumnKey));
    const available = columns.filter((column) => (group === 'all' || column.group === group) &&
        column.search
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase().trim()));
    const valid = caseVoucherExportSettingsSchema.safeParse(draft).success;
    const toggle = (column) => {
        const key = exportColumnKey(column);
        setDraft({
            ...draft,
            columns: chosen.has(key)
                ? draft.columns.filter((item) => exportColumnKey(item) !== key)
                : [...draft.columns, column],
        });
    };
    return (React.createElement(Modal, { maxWidth: "1080px", header: lang.deposits.customizeExport, onToggle: props.onClose, footer: React.createElement(Footer, null,
            React.createElement(StorageNote, null,
                React.createElement(Check, null),
                lang.deposits.exportStoredLocally),
            React.createElement(FooterActions, null,
                React.createElement(Button, { theme: "text", onClick: props.onClose }, lang.misc.cancel),
                React.createElement(Button, { disabled: !valid, loading: props.saving, onClick: () => props.onSave(draft) }, lang.deposits.saveExportSettings))) },
        React.createElement(Intro, null,
            React.createElement(AccountLabel, null, props.accountName),
            React.createElement("p", null, lang.deposits.exportEditorDescription)),
        React.createElement(Options, null,
            React.createElement(FormatControl, null,
                React.createElement(OptionLabel, null, lang.deposits.exportFormat),
                React.createElement(Segments, null, [ExportType.Csv, ExportType.Xlsx].map((format) => (React.createElement(Segment, { key: format, type: "button", "aria-pressed": draft.exportType === format, onClick: () => setDraft({ ...draft, exportType: format }) }, format))))),
            React.createElement(MarkOption, null,
                React.createElement(Checkbox, { checked: draft.markAsExported, onChange: (event) => setDraft({ ...draft, markAsExported: event.target.checked }), placeholder: lang.deposits.markAsExported }))),
        React.createElement(Editor, null,
            React.createElement(Library, null,
                React.createElement(PaneHeader, null,
                    React.createElement("h3", null, lang.deposits.availableExportColumns),
                    React.createElement(Count, null, columns.length)),
                React.createElement(SearchBox, null,
                    React.createElement(Search, null),
                    React.createElement("input", { "aria-label": lang.deposits.searchExportColumns, placeholder: lang.deposits.searchExportColumns, value: search, onChange: (event) => setSearch(event.target.value) })),
                React.createElement(Categories, null, [{ id: 'all', label: lang.misc.all }, ...groups].map((category) => (React.createElement(Category, { type: "button", key: category.id, "aria-pressed": group === category.id, onClick: () => setGroup(category.id) }, category.label)))),
                React.createElement(LibraryList, null,
                    groups.map((category) => {
                        const items = available.filter((column) => column.group === category.id);
                        return (items.length > 0 && (React.createElement("section", { key: category.id },
                            React.createElement(GroupLabel, null, category.label),
                            items.map((item) => {
                                const key = exportColumnKey(item.column);
                                return (React.createElement(AvailableRow, { type: "button", key: key, "aria-pressed": chosen.has(key), disabled: !chosen.has(key) && draft.columns.length >= 100, onClick: () => toggle(item.column) },
                                    React.createElement("span", null, item.label),
                                    React.createElement(SelectionMark, { $selected: chosen.has(key) }, chosen.has(key) ? React.createElement(Check, null) : React.createElement(Plus, null))));
                            }))));
                    }),
                    available.length === 0 && React.createElement(Empty, null, lang.search.noResults),
                    !h.hasData(propertyTypes) &&
                        (h.isError(propertyTypes) ? (React.createElement(Empty, { role: "alert" }, lang.misc.loadingError)) : (React.createElement(PlaceholderLoader, null))))),
            React.createElement(Selection, null,
                React.createElement(PaneHeader, null,
                    React.createElement("h3", null, lang.deposits.exportColumns),
                    React.createElement(Count, null, draft.columns.length),
                    React.createElement(Reset, { type: "button", onClick: () => setDraft({
                            ...draft,
                            columns: defaultExportSettings(props.deposits).columns,
                        }) }, lang.deposits.resetExportColumns)),
                React.createElement(Hint, null, lang.deposits.exportColumnHint),
                React.createElement(SelectedList, null,
                    draft.columns.length === 0 && (React.createElement(Empty, null, lang.deposits.chooseExportColumns)),
                    React.createElement(DraggableList, { onChange: (items) => setDraft({
                            ...draft,
                            columns: items.map((item) => draft.columns.find((column) => exportColumnKey(column) === item.id)),
                        }), list: draft.columns.map((column, index) => {
                            const key = exportColumnKey(column);
                            const original = columns.find((item) => exportColumnKey(item.column) === key);
                            const label = column.label ??
                                (column.type === 'field'
                                    ? labels[column.field]
                                    : (original?.label ?? ''));
                            return {
                                id: key,
                                content: (React.createElement(SelectedRow, null,
                                    React.createElement(DragHandle, { title: lang.deposits.exportColumnHint },
                                        React.createElement(Menu, null)),
                                    React.createElement(Position, null, index + 1),
                                    React.createElement(ColumnName, null,
                                        React.createElement(Input, { aria: {
                                                'aria-label': `${lang.deposits.exportColumnName} ${index + 1}`,
                                            }, value: label, maxLength: 200, onChange: (event) => setDraft({
                                                ...draft,
                                                columns: draft.columns.map((entry, position) => position === index
                                                    ? { ...entry, label: event.target.value }
                                                    : entry),
                                            }) }),
                                        React.createElement(SourceLabel, null, original
                                            ? `${groups.find((category) => category.id === original.group)?.label} · ${original.label}`
                                            : label)),
                                    React.createElement(RowActions, null,
                                        React.createElement(IconButton, { type: "button", "aria-label": `${lang.deposits.removeExportColumn}: ${label}`, title: lang.deposits.removeExportColumn, onClick: () => toggle(column) },
                                            React.createElement(X, null))))),
                            };
                        }) }))))));
};
export default CaseVoucherExportEditor;
const Intro = styled.div `
  margin: -5px 0 20px;
  p {
    margin: 8px 0 0;
    color: #72738a;
    font-size: 13px;
  }
`;
const AccountLabel = styled.span `
  display: inline-block;
  padding: 5px 10px;
  border-radius: 6px;
  background: #f0edff;
  color: ${THEME.COLOR_ACCENT};
  font-size: 12px;
  font-weight: 700;
`;
const Options = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  background: #f7f8fc;
  border: 1px solid #eaebf2;
  border-radius: 10px;
  margin-bottom: 20px;
`;
const FormatControl = styled.div `
  display: flex;
  align-items: center;
  gap: 18px;
`;
const OptionLabel = styled.span `
  font-size: 12px;
  font-weight: 700;
`;
const Segments = styled.div `
  display: flex;
  padding: 3px;
  border-radius: 7px;
  background: #e9eaf1;
`;
const Segment = styled.button `
  border: 0;
  border-radius: 5px;
  padding: 7px 16px;
  background: transparent;
  color: #72738a;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &[aria-pressed='true'] {
    background: white;
    color: ${THEME.COLOR_PRIMARY};
    box-shadow: 0 1px 4px #200d5414;
  }
`;
const MarkOption = styled.div `
  .checkbox-wrapper {
    margin: 0;
    font-size: 12px;
  }
`;
const Editor = styled.div `
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  border: 1px solid #e5e6ef;
  border-radius: 10px;
  overflow: hidden;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
const Library = styled.div `
  padding: 16px;
  background: #fafbfe;
  border-right: 1px solid #e5e6ef;
  min-width: 0;
`;
const Selection = styled.div `
  padding: 16px;
  min-width: 0;
`;
const PaneHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  h3 {
    margin: 0;
    font-size: 14px;
  }
`;
const Count = styled.span `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 23px;
  padding: 0 6px;
  border-radius: 6px;
  background: #efecff;
  color: ${THEME.COLOR_ACCENT};
  font-size: 11px;
  font-weight: 700;
`;
const SearchBox = styled.label `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  border: 1px solid #e1e3ec;
  border-radius: 7px;
  background: white;
  color: #9293a5;
  &:focus-within {
    border-color: ${THEME.COLOR_ACCENT};
  }
  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: 12px;
    color: ${THEME.COLOR_PRIMARY};
  }
`;
const Categories = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 12px 0 4px;
`;
const Category = styled.button `
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 5px 7px;
  background: transparent;
  color: #72738a;
  font: inherit;
  font-size: 11px;
  cursor: pointer;
  &[aria-pressed='true'] {
    color: ${THEME.COLOR_ACCENT};
    background: #efecff;
    border-color: #ded8ff;
  }
`;
const LibraryList = styled.div `
  height: 390px;
  max-height: 42vh;
  min-height: 200px;
  overflow-y: auto;
  padding-right: 5px;
`;
const GroupLabel = styled.h4 `
  font-size: 10px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #898a9c;
  margin: 18px 0 7px;
`;
const AvailableRow = styled.button `
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 9px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  font: inherit;
  font-size: 12px;
  color: ${THEME.COLOR_PRIMARY};
  cursor: pointer;
  &:hover {
    background: #efecff;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const SelectionMark = styled.span `
  display: inline-flex;
  padding: 3px;
  border-radius: 5px;
  flex-shrink: 0;
  color: ${(props) => (props.$selected ? THEME.COLOR_ACCENT : '#9293a5')};
  background: ${(props) => (props.$selected ? '#efecff' : 'transparent')};
`;
const Reset = styled.button `
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${THEME.COLOR_ACCENT};
  font: inherit;
  font-size: 11px;
  cursor: pointer;
`;
const Hint = styled.p `
  color: #898a9c;
  font-size: 11px;
  margin: 0 0 13px;
`;
const SelectedList = styled.div `
  height: 459px;
  max-height: 49vh;
  min-height: 250px;
  overflow-y: auto;
  padding-right: 5px;
`;
const SelectedRow = styled.div `
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 8px;
  margin-bottom: 5px;
  border: 1px solid #e9eaf2;
  border-radius: 7px;
  background: white;
  &:hover {
    border-color: #c9c1fb;
    background: #fdfcff;
  }
`;
const DragHandle = styled.span `
  display: inline-flex;
  color: #b0b1c2;
  cursor: grab;
  font-size: 13px;
`;
const Position = styled.span `
  color: #9293a5;
  font-size: 10px;
  min-width: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;
const ColumnName = styled.div `
  flex: 1;
  min-width: 0;
  .iris-input-container {
    margin: 0;
  }
  input {
    font-size: 12px;
    margin-bottom: 0;
    font-weight: 600;
    padding: 3px 4px !important;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 4px;
    min-height: 0;
    height: 27px;
  }
  input:hover {
    border-color: #e5e2f7;
  }
  input:focus {
    border-color: ${THEME.COLOR_ACCENT};
    background: white;
  }
`;
const SourceLabel = styled.div `
  padding-left: 5px;
  color: #898a9c;
  font-size: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const RowActions = styled.div `
  display: flex;
  align-items: center;
  gap: 2px;
`;
const IconButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 27px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #898a9c;
  cursor: pointer;
  &:hover {
    background: #efecff;
    color: ${THEME.COLOR_ACCENT};
  }
  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;
const Empty = styled.div `
  padding: 32px 16px;
  font-size: 12px;
  text-align: center;
  color: #898a9c;
`;
const Footer = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const FooterActions = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StorageNote = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  color: #898a9c;
`;
//# sourceMappingURL=CaseVoucherExportEditor.js.map